// Activity measurement engine (spec §2.1 / §2.2).
//
// Not a 1-second ticker: it accumulates active time by classifying the interval
// since the previous evaluation, using performance.now() deltas. Evaluation is
// event-driven (visibility/focus/media/interaction) plus a 15s safety timer.
//
// The class is DOM-free and deterministic (inject `now`) so it can be unit
// tested with fake timers. `bindActivityMeterToDom` wires it to real events.

export type LessonType = 'video' | 'text' | 'assessment' | 'pdf'

export interface MeterSignalConfig {
  // How long after the last interaction a "reading / thinking" interval still
  // counts as estimated time.
  estimatedWindowMs: number
  // How long after the last interaction an interval counts as confirmed.
  // 0 means this lesson type has no interaction-based confirmed signal.
  confirmedWindowMs: number
  // Video: confirmed time is driven by media-playing, not interaction recency.
  usesMediaSignal: boolean
}

export const SIGNAL_CONFIGS: Record<LessonType, MeterSignalConfig> = {
  video: { estimatedWindowMs: 120_000, confirmedWindowMs: 0, usesMediaSignal: true },
  text: { estimatedWindowMs: 600_000, confirmedWindowMs: 120_000, usesMediaSignal: false },
  assessment: { estimatedWindowMs: 300_000, confirmedWindowMs: 120_000, usesMediaSignal: false },
  pdf: { estimatedWindowMs: 600_000, confirmedWindowMs: 0, usesMediaSignal: false }
}

// Cap a single interval to guard against timer throttling / sleep-resume spikes.
export const MAX_INTERVAL_MS = 60_000
export const EVAL_INTERVAL_MS = 15_000
export const INTERACTION_THROTTLE_MS = 5_000

export interface MeterSnapshot {
  confirmedActiveSeconds: number
  estimatedActiveSeconds: number
  interactionCount: number
  maxProgress: number
  diag: MeterDiagInterval[]
}

export type MeterClassification = 'confirmed' | 'estimated' | 'away'
// Compact tuple keeps 200 diagnostic intervals comfortably below the 16KB API limit.
export type MeterDiagInterval = [atMs: number, classification: MeterClassification, deltaMs: number]

export class ActivityMeter {
  private readonly config: MeterSignalConfig
  private readonly now: () => number

  private lastEvalAt: number
  private lastInteractionAt: number
  private visible = true
  private focused = true
  private mediaPlaying = false

  private confirmedMs = 0
  private estimatedMs = 0
  private interactionCount = 0
  private maxProgress = 0
  private readonly diag: MeterDiagInterval[] = []

  constructor(lessonType: LessonType, opts: { now?: () => number } = {}) {
    this.config = SIGNAL_CONFIGS[lessonType] ?? SIGNAL_CONFIGS.text
    this.now = opts.now ?? (() => performance.now())
    const t = this.now()
    this.lastEvalAt = t
    // Seed interaction time so passive reading counts within the window from the start.
    this.lastInteractionAt = t
  }

  // Classify the interval since the last evaluation and accumulate it.
  evaluate(): void {
    const now = this.now()
    let delta = now - this.lastEvalAt
    if (delta < 0) delta = 0
    if (delta > MAX_INTERVAL_MS) delta = MAX_INTERVAL_MS

    this.classify(delta, now)
    this.lastEvalAt = now
  }

  private classify(delta: number, now: number): void {
    if (delta <= 0) return
    let classification: MeterClassification = 'away'

    if (!this.visible) {
      // away: no accrual
    } else if (this.config.usesMediaSignal && this.mediaPlaying) {
      // Playback in a visible page counts even without window focus: pressing
      // play inside the YouTube/Vimeo iframe moves focus into the iframe and
      // fires `blur` on this window for the whole time the video plays.
      this.confirmedMs += delta
      classification = 'confirmed'
    } else if (!this.focused) {
      // away: no accrual
    } else if (this.isConfirmed(now)) {
      this.confirmedMs += delta
      classification = 'confirmed'
    } else if (now - this.lastInteractionAt <= this.config.estimatedWindowMs) {
      this.estimatedMs += delta
      classification = 'estimated'
    }
    // else: idle beyond the estimated window -> treated as away

    this.diag.push([now, classification, delta])
    if (this.diag.length > 200) this.diag.shift()
  }

  private isConfirmed(now: number): boolean {
    if (this.config.usesMediaSignal) return this.mediaPlaying
    if (this.config.confirmedWindowMs <= 0) return false
    return now - this.lastInteractionAt <= this.config.confirmedWindowMs
  }

  setVisible(visible: boolean): void {
    if (visible === this.visible) return
    if (!visible) {
      this.evaluate() // count up to the moment it goes hidden
      this.visible = false
    } else {
      this.visible = true
      this.lastEvalAt = this.now() // never count the hidden gap
    }
  }

  setFocused(focused: boolean): void {
    if (focused === this.focused) return
    if (!focused) {
      this.evaluate()
      this.focused = false
    } else {
      this.focused = true
      this.lastEvalAt = this.now()
    }
  }

  setMediaPlaying(playing: boolean): void {
    if (playing === this.mediaPlaying) return
    this.evaluate() // classify the interval under the old media state first
    this.mediaPlaying = playing
  }

  recordInteraction(): void {
    this.evaluate() // close the previous interval under the old interaction time
    this.interactionCount += 1
    this.lastInteractionAt = this.now()
  }

  setProgress(progress: number): void {
    const clamped = Math.min(Math.max(progress, 0), 1)
    if (clamped > this.maxProgress) this.maxProgress = clamped
  }

  snapshot(): MeterSnapshot {
    this.evaluate() // fold in the current interval so the snapshot is fresh
    return {
      confirmedActiveSeconds: Math.floor(this.confirmedMs / 1000),
      estimatedActiveSeconds: Math.floor(this.estimatedMs / 1000),
      interactionCount: this.interactionCount,
      maxProgress: this.maxProgress,
      diag: [...this.diag]
    }
  }
}

const MEANINGFUL_EVENTS = ['scroll', 'keydown', 'pointerdown'] as const

// Wire a meter to real DOM events. Returns a detach function.
export function bindActivityMeterToDom(meter: ActivityMeter): () => void {
  let lastInteractionEmit = 0

  const onInteraction = () => {
    const t = performance.now()
    if (t - lastInteractionEmit < INTERACTION_THROTTLE_MS) return
    lastInteractionEmit = t
    meter.recordInteraction()
  }

  // document.hasFocus() stays true while focus sits inside a child iframe (the
  // video player, an exam) and turns false only when the user has left for
  // another tab or app. A bare window `blur` can't tell those two apart.
  const syncFocus = () => meter.setFocused(document.hasFocus())
  let blurCheck: number | undefined

  const onVisibility = () => meter.setVisible(document.visibilityState === 'visible')
  const onFocus = () => meter.setFocused(true)
  // Focus only lands in the iframe after `blur` has dispatched; check on the next task.
  const onBlur = () => {
    window.clearTimeout(blurCheck)
    blurCheck = window.setTimeout(syncFocus, 0)
  }
  const timer = window.setInterval(() => {
    syncFocus()
    meter.evaluate()
  }, EVAL_INTERVAL_MS)

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onFocus)
  window.addEventListener('blur', onBlur)
  MEANINGFUL_EVENTS.forEach((evt) =>
    window.addEventListener(evt, onInteraction, { passive: true })
  )

  return () => {
    window.clearInterval(timer)
    window.clearTimeout(blurCheck)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', onFocus)
    window.removeEventListener('blur', onBlur)
    MEANINGFUL_EVENTS.forEach((evt) => window.removeEventListener(evt, onInteraction))
  }
}
